import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import showToast from '../../components/Toast';
import { FiLock, FiEye, FiEyeOff, FiArrowRight, FiShield, FiCheck, FiAlertTriangle, FiArrowLeft } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import './PasswordSetup.css';

const API_BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

function PasswordSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    // Check if user is already logged in and has password set
    const token = config.getAuthToken();
    const userId = config.getUserId();
    
    if (!token || !userId) {
      navigate('/auth');
      return;
    }

    // Get user info from URL params or localStorage
    const userData = config.getUserData();
    
    // Check if company name is missing first
    if (userData && (!userData.CompanyName || userData.CompanyName.trim() === '')) {
      navigate('/auth/company-setup');
      return;
    }
    
    if (userData && userData.Password && userData.Password !== '') {
      // User already has password set, redirect to dashboard
      showToast.info('You already have a password set. Redirecting to dashboard...');
      setTimeout(() => {
        navigate('/');
      }, 2000);
      return;
    }

    setUserInfo(userData);
  }, [navigate]);

  // Warn user but allow navigation
  useEffect(() => {
    const handlePopState = (e) => {
      if (!passwordSet) {
        setShowExitDialog(true);
        // Push the current state back to show dialog
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Push a state to catch back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [passwordSet]);

  const passwordValidationSchema = {
    password: (value) => {
      if (!value) return 'Password is required';
      if (value.length < 8) return 'Password must be at least 8 characters long';
      if (!/(?=.*[a-z])/.test(value)) return 'Password must contain at least one lowercase letter';
      if (!/(?=.*[A-Z])/.test(value)) return 'Password must contain at least one uppercase letter';
      if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number';
      if (!/(?=.*[@$!%*?&])/.test(value)) return 'Password must contain at least one special character (@$!%*?&)';
      return null;
    },
    confirmPassword: (value, values) => {
      if (!value) return 'Please confirm your password';
      if (value !== values.password) return 'Passwords do not match';
      return null;
    }
  };

  const handlePasswordSetup = async (values, { setSubmitting, setFieldError }) => {
    try {
      setLoading(true);
      
      const userId = config.getUserId();
      const token = config.getAuthToken();
      
      if (!userId || !token) {
        showToast.error('Authentication required. Please log in again.');
        navigate('/auth');
        return;
      }

      const response = await AxiosInstance.put(`${API_BASE_URL}/user/update-password`, {
        UserId: userId,
        Password: values.password,
        ConfirmPassword: values.confirmPassword
      });

      if (response.data.statusCode === 200) {
        showToast.success('Password set successfully! Welcome to your account.');
        
        // Mark password as set
        setPasswordSet(true);
        
        // Update user data in localStorage
        const userData = config.getUserData();
        if (userData) {
          userData.Password = values.password;
          config.setUserData(userData);
        }
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        showToast.error(response.data.message || 'Failed to set password. Please try again.');
      }
    } catch (error) {
      console.error('Password setup error:', error);
      if (error.response?.data?.message) {
        showToast.error(error.response.data.message);
      } else {
        showToast.error('Failed to set password. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleExitConfirm = () => {
    // Allow navigation but warn user
    setShowExitDialog(false);
    showToast.warning('Password not set! You will need to set it later to login with password. Currently you can only login with Google.');
    setTimeout(() => {
      navigate('/');
    }, 500);
  };

  const handleExitCancel = () => {
    setShowExitDialog(false);
  };

  const handleBackClick = () => {
    if (!passwordSet) {
      setShowExitDialog(true);
    } else {
      navigate('/');
    }
  };

  if (!userInfo) {
    return (
      <div className="password-setup-container">
        <div className="password-setup-form-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-setup-container">
      {/* Background Section */}
      <div className="password-setup-background">
        <div className="password-setup-background-overlay"></div>
        <div className="password-setup-background-content">
          <div className="welcome-icon">
            <FiShield />
          </div>
          <h1>Welcome!</h1>
          <p>Complete your account setup by creating a secure password</p>
          
          <div className="password-setup-features">
            <div className="feature">
              <div className="feature-icon">
                <FiCheck />
              </div>
              <span>Secure your account with a strong password</span>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <FiCheck />
              </div>
              <span>Access all features after setup</span>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <FiCheck />
              </div>
              <span>Quick and easy setup process</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="password-setup-form-container">
        <div className="password-setup-form">
          {/* Back Button */}
          <button 
            type="button" 
            className="password-setup-back-button"
            onClick={handleBackClick}
          >
            <FiArrowLeft />
            Back
          </button>

          <div className="password-setup-header">
            <h2>Set Your Password</h2>
            <p>Hello {userInfo.FirstName}! Please create a secure password to complete your account setup.</p>
            <div className="password-warning-banner">
              <FiAlertTriangle />
              <span>Password is required for future logins. Without it, you can only login with Google.</span>
            </div>
          </div>

          <Formik
            initialValues={{
              password: '',
              confirmPassword: ''
            }}
            validate={(values) => {
              const errors = {};
              
              const passwordError = passwordValidationSchema.password(values.password);
              if (passwordError) errors.password = passwordError;
              
              const confirmPasswordError = passwordValidationSchema.confirmPassword(values.confirmPassword, values);
              if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;
              
              return errors;
            }}
            onSubmit={handlePasswordSetup}
          >
            {({ values, errors, touched, isSubmitting }) => (
              <Form className="password-setup-form-fields">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-group">
                    <div className="input-icon">
                      <FiLock />
                    </div>
                    <Field
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      className={`form-input ${errors.password && touched.password ? 'error' : ''}`}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <div className="error-message">
                    <ErrorMessage name="password" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="input-group">
                    <div className="input-icon">
                      <FiLock />
                    </div>
                    <Field
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      className={`form-input ${errors.confirmPassword && touched.confirmPassword ? 'error' : ''}`}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <div className="error-message">
                    <ErrorMessage name="confirmPassword" />
                  </div>
                </div>

                <div className="password-requirements">
                  <h4>Password Requirements:</h4>
                  <ul>
                    <li className={values.password.length >= 8 ? 'valid' : ''}>
                      At least 8 characters long
                    </li>
                    <li className={/(?=.*[a-z])/.test(values.password) ? 'valid' : ''}>
                      One lowercase letter
                    </li>
                    <li className={/(?=.*[A-Z])/.test(values.password) ? 'valid' : ''}>
                      One uppercase letter
                    </li>
                    <li className={/(?=.*\d)/.test(values.password) ? 'valid' : ''}>
                      One number
                    </li>
                    <li className={/(?=.*[@$!%*?&])/.test(values.password) ? 'valid' : ''}>
                      One special character (@$!%*?&)
                    </li>
                  </ul>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || loading || Object.keys(errors).length > 0}
                  className="password-setup-button"
                >
                  {loading ? (
                    <div className="button-loading">
                      <div className="button-spinner"></div>
                      Setting up...
                    </div>
                  ) : (
                    <>
                      Complete Setup
                      <FiArrowRight />
                    </>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      {showExitDialog && (
        <div className="exit-dialog-overlay" onClick={handleExitCancel}>
          <div className="exit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="exit-dialog-icon">
              <FiAlertTriangle />
            </div>
            <h3>Password Not Set</h3>
            <p>
              You haven't set your password yet. Setting a password is <strong>important</strong> because:
            </p>
            <ul className="exit-dialog-reasons">
              <li>You'll need it to login with email and password</li>
              <li>Without it, you can only login using Google</li>
              <li>It provides an extra layer of security</li>
            </ul>
            <p className="exit-dialog-warning">
              <strong>You can set it later from Settings, but it's recommended to set it now.</strong>
            </p>
            <div className="exit-dialog-buttons">
              <button 
                className="exit-dialog-continue"
                onClick={handleExitCancel}
              >
                Continue Setup
              </button>
              <button 
                className="exit-dialog-leave"
                onClick={handleExitConfirm}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PasswordSetup;
